import React, { useCallback, useEffect, useMemo } from 'react'
import { Col, Form, Row, Spin } from 'antd'
import PropTypes from 'prop-types'

import {
  DirtyPromptForm,
  FormButtons,
  FormSelect,
  FormTextareaInput,
} from '../../../common/components/sections'
import {
  StyledPageHeader,
  StyledPageTitle,
} from '../../../common/components/styled'
import { PERMISSIONS } from '../../../common/constants'
import {
  createSelectOptions,
  showErrorNotification,
} from '../../../common/helpers'
import { useFormDirty, useUserHasPermissions } from '../../../common/hooks'
import { idPropType } from '../../../common/prop-types'
import { generateArrayOfNumbers } from '../../../common/utils'

import { useUpdateLocationTypeCategoryDetailsMutation } from '../../hooks'
import { LocationTypeCategoryIsMainCheckbox } from '../sections'

const LocationTypeCategorySimpleDetailsForm = ({
  id,
  name,
  description,
  position,
  isMain,
  maxPosition,
}) => {
  const [form] = Form.useForm()
  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const isFormEnabled = useUserHasPermissions([
    PERMISSIONS.LOCATION_TYPE.UPDATE_DETAILS,
  ])

  const [
    updateLocationTypeCategoryDetails,
    { isLoading: isSaveLoading, error: saveError },
  ] = useUpdateLocationTypeCategoryDetailsMutation()

  const initialValues = useMemo(
    () => ({
      description,
      position,
      isMain,
    }),
    [description, isMain, position],
  )

  const positionOptions = useMemo(
    () => createSelectOptions(generateArrayOfNumbers(maxPosition)),
    [maxPosition],
  )

  const handleSaveData = useCallback(
    data => updateLocationTypeCategoryDetails(id, data),
    [id, updateLocationTypeCategoryDetails],
  )

  useEffect(() => {
    if (saveError) {
      showErrorNotification({
        title: 'Location Type Category description error',
        description: saveError,
      })
    }
  }, [saveError])

  useEffect(() => {
    resetForm()
  }, [description, position, isMain, resetForm])

  return isSaveLoading ? (
    <Spin />
  ) : (
    <DirtyPromptForm
      isDirty={isDirty}
      form={form}
      initialValues={initialValues}
      name="editLocationTypeCategory"
      onFinish={handleSaveData}
      autoComplete="off"
      onFieldsChange={setToDirty}
    >
      <StyledPageHeader>
        <StyledPageTitle>{name}</StyledPageTitle>
      </StyledPageHeader>

      <Row gutter={[16]}>
        <Col span={12}>
          <FormTextareaInput
            disabled={!isFormEnabled}
            name="description"
            label="Description"
            labelAbove
            placeholder="Input description..."
            autoSize
          />
          <LocationTypeCategoryIsMainCheckbox
            disabled={!isFormEnabled}
            name="isMain"
            label="Is category main"
          />
          <FormSelect
            disabled={!isFormEnabled}
            name="position"
            labelAbove
            label="Position"
            showSearch={false}
            allowClear={false}
            options={positionOptions}
            placeholder="Select position..."
          />
        </Col>
      </Row>
      <FormButtons
        permissions={[PERMISSIONS.LOCATION_TYPE.UPDATE_DETAILS]}
        isDisabled={!isDirty}
        onReset={resetForm}
      />
    </DirtyPromptForm>
  )
}

LocationTypeCategorySimpleDetailsForm.propTypes = {
  id: idPropType.isRequired,
  name: PropTypes.string.isRequired,
  description: PropTypes.string,
  position: PropTypes.number.isRequired,
  maxPosition: PropTypes.number.isRequired,
  isMain: PropTypes.bool.isRequired,
}

LocationTypeCategorySimpleDetailsForm.defaultProps = {
  description: undefined,
}

export default LocationTypeCategorySimpleDetailsForm
